import { useCallback } from 'react';

import { type InfiniteData, useQueryClient } from '@tanstack/react-query';

import { groupsCacheKeys } from '@pkg/hooks';

import { type GetGroupsResponse } from '@app/client/common/structs/groups/groups';

export const useUpdateCache = (groupId?: string) => {
    const queryClient = useQueryClient();

    return useCallback(async () => {
        // refetch individual group
        await queryClient.refetchQueries(groupsCacheKeys.group(groupId));

        // since the order of GET /groups change if you follow/unfollow, we
        // only want to update the cache to reflect the new follow status
        // for this individual group, otherwise the group jumps to a new place
        // in the list, not good
        type GroupsCache = InfiniteData<GetGroupsResponse> | undefined;
        queryClient.setQueriesData<GroupsCache>(groupsCacheKeys.groups(), (data) => {
            // loop through every page and group to find the one we have updated
            const pages =
                data?.pages.map((page) => ({
                    ...page,
                    data: page?.data.map((d) => {
                        const currentFollowerCount = d.follower_count ?? 0;
                        const newFollowerCount = d.following ? currentFollowerCount - 1 : currentFollowerCount + 1;
                        const currentFollowerCountWeek = d.follower_count_week ?? 0;
                        const newFollowerCountWeek = d.following
                            ? currentFollowerCountWeek - 1
                            : currentFollowerCountWeek + 1;

                        return {
                            ...d,
                            following: d.id === groupId ? !d.following : d.following,
                            follower_count: newFollowerCount,
                            follower_count_week: newFollowerCountWeek,
                        };
                    }),
                })) ?? [];

            return {
                pages,
                pageParams: data?.pageParams ?? [],
            };
        });
    }, [queryClient, groupId]);
};
